import { useMutation } from '@tanstack/react-query';

import { OEM_REPORT_KEY_ENUM } from '@ekhodealer/ekho-common';

import type { OrderReportItem } from '../atoms/ordersAtoms';
import type { PaymentReconciliationReportItem } from '../atoms/paymentReconciliationAtoms';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { reportsEndpoints } from '@config/server/apiEndpoints';

/**
 * Union type of all possible report items
 */
export type ReportItem = OrderReportItem | PaymentReconciliationReportItem;

/**
 * Parameters for generating a report
 */
export interface PostReportParams<
	T extends keyof typeof OEM_REPORT_KEY_ENUM = keyof typeof OEM_REPORT_KEY_ENUM,
> {
	reportKey: (typeof OEM_REPORT_KEY_ENUM)[T];
	reportPeriodStart?: string;
	reportPeriodEnd?: string;
}

/**
 * API function to generate a report
 * Returns different array types based on the report key
 * Uses function overloads to ensure TypeScript narrows the return type correctly
 */
export async function postReport(
	servicesProviderId: string,
	params: PostReportParams & { reportKey: typeof OEM_REPORT_KEY_ENUM.SALES_BY_CUSTOMER_NAME },
): Promise<OrderReportItem[]>;
export async function postReport(
	servicesProviderId: string,
	params: PostReportParams & { reportKey: typeof OEM_REPORT_KEY_ENUM.OUTGOING_PAYMENTS },
): Promise<PaymentReconciliationReportItem[]>;
export async function postReport(
	servicesProviderId: string,
	params: PostReportParams,
): Promise<ReportItem[]> {
	const response = await apiClient.post<ReportItem[]>(
		reportsEndpoints.generate(servicesProviderId),
		params,
		{ timeout: 180_000 }, // 180 seconds timeout for report generation
	);

	// The backend returns { success: true, data: ReportItem[], ... }
	// apiClient.post returns ApiResponse<T> which has a data property
	// The response structure is: { success: boolean, data: T, message?: string, ... }

	// Check if response.data exists and is an array (including empty arrays)
	if (response && 'data' in response) {
		const data = response.data;
		if (Array.isArray(data)) {
			return data; // This handles both empty [] and non-empty arrays
		}
	}

	// Handle case where data might be nested (shouldn't happen but being defensive)
	if (response && typeof response === 'object' && 'data' in response) {
		const data = (response as unknown as { data: unknown }).data;
		if (data && typeof data === 'object' && 'data' in data) {
			const nestedData = (data as unknown as { data: ReportItem[] }).data;
			if (Array.isArray(nestedData)) {
				return nestedData;
			}
		}
	}

	// Fallback to empty array if data structure is unexpected
	console.warn(
		'postReport - unexpected data structure, returning empty array. Response:',
		response,
	);
	return [];
}

/**
 * Hook for generating a report
 * Pass the reportKey as a parameter to get properly typed mutateAsync
 * When reportKey is provided, mutateAsync will return the specific type for that report
 */
export const usePostReports = (
	reportKey?: (typeof OEM_REPORT_KEY_ENUM)[keyof typeof OEM_REPORT_KEY_ENUM],
) => {
	const servicesProviderId = useCurrentServicesProviderId();

	const mutation = useMutation({
		mutationFn: async (params: PostReportParams | Omit<PostReportParams, 'reportKey'>) => {
			if (!servicesProviderId) {
				throw new Error('Cannot generate report without services provider ID');
			}
			const finalParams = reportKey
				? ({ ...params, reportKey } as PostReportParams)
				: (params as PostReportParams);
			return postReport(servicesProviderId, finalParams);
		},
	});

	// Type assertions based on reportKey - postReport overloads ensure correct runtime types
	switch (reportKey) {
		case OEM_REPORT_KEY_ENUM.SALES_BY_CUSTOMER_NAME:
			return mutation as unknown as ReturnType<
				typeof useMutation<
					OrderReportItem[],
					Error,
					Omit<PostReportParams<'SALES_BY_CUSTOMER_NAME'>, 'reportKey'>
				>
			>;
		case OEM_REPORT_KEY_ENUM.OUTGOING_PAYMENTS:
			return mutation as unknown as ReturnType<
				typeof useMutation<
					PaymentReconciliationReportItem[],
					Error,
					Omit<PostReportParams<'OUTGOING_PAYMENTS'>, 'reportKey'>
				>
			>;
		default:
			return mutation as ReturnType<
				typeof useMutation<ReportItem[], Error, PostReportParams>
			>;
	}
};
